import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { perspectiveTransform, type Pt } from '@/utils/projection';

/** Геометрия стеллажа на кадре камеры для «в оригинальных пропорциях» (Алексей 21.10 #1671). */
export interface OrigGeom {
  /** 4 точки полигона стеллажа, нормированные к кадру (0..1), в порядке кропа:
   *  верх-лево, верх-право, низ-право, низ-лево (как fix_perspective_and_crop). */
  quad: Pt[];
  /** Описывающий прямоугольник полигона, нормированный к кадру. */
  bbox: { x0: number; y0: number; x1: number; y1: number };
}

/** Ширина/высота описывающего прямоугольника на кадре с пропорцией frameAspect. */
export function origAspect(g: OrigGeom, frameAspect: number): number {
  const w = g.bbox.x1 - g.bbox.x0;
  const h = g.bbox.y1 - g.bbox.y0;
  return Math.min(6, Math.max(0.2, (w / h) * frameAspect));
}

/** Полигон стеллажа → геометрия; null, если это не четырёхугольник.
 *  `anyPolygon` (столы, Алексей 26.10 #2638): полигон из любого числа точек (≥3). Четырёхугольник
 *  стола кропается той же гомографией, что стеллаж (transform_single_area), поэтому без кадра кроп
 *  так же разворачивается обратно на полигон (#2787: растягивался в рамку); иначе quad = углы рамки. */
export function origGeomFromPolygon(
  pts: number[][] | null | undefined,
  opts?: { anyPolygon?: boolean },
): OrigGeom | null {
  if (!pts || (opts?.anyPolygon ? pts.length < 3 : pts.length !== 4)) return null;
  const clamped = pts.map(([x, y]) => [Math.min(1, Math.max(0, x)), Math.min(1, Math.max(0, y))] as Pt);
  const xs = clamped.map((p) => p[0]);
  const ys = clamped.map((p) => p[1]);
  const bbox = { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) };
  if (!(bbox.x1 - bbox.x0 > 1e-4) || !(bbox.y1 - bbox.y0 > 1e-4)) return null;
  // Кроп стола без перспективы (не 4 точки) — это и есть описывающий прямоугольник (table_pipeline._polygon_to_bbox).
  const quad: Pt[] = clamped.length === 4
    ? clamped
    : [[bbox.x0, bbox.y0], [bbox.x1, bbox.y0], [bbox.x1, bbox.y1], [bbox.x0, bbox.y1]];
  return { quad, bbox };
}

/**
 * Фото стеллажа так, как он выглядит на кадре камеры: описывающий прямоугольник полигона.
 *  • кадр сохранился → этот прямоугольник вырезан из кадра (с контекстом вокруг стеллажа);
 *  • кадра нет → выровненный кроп обратно «натянут» на полигон перспективным преобразованием
 *    (CSS matrix3d); недостающие области прямоугольника — из `bgFrameUrl` (ближайший кадр камеры,
 *    столы #2789: «черным не надо»), без него — чёрные.
 * Без canvas: не нужен CORS у картинок, браузер сам масштабирует.
 */
export function OrigCropView({
  cropUrl,
  frameUrl,
  bgFrameUrl,
  geom,
  onLoad,
}: {
  cropUrl: string | null;
  frameUrl?: string | null;
  bgFrameUrl?: string | null;
  geom: OrigGeom;
  onLoad?: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const [frameFailed, setFrameFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const { bbox } = geom;
  const bw = bbox.x1 - bbox.x0;
  const bh = bbox.y1 - bbox.y0;
  const useFrame = !!frameUrl && !frameFailed;
  const [bgFailed, setBgFailed] = useState(false);
  const frameBoxStyle: CSSProperties = {
    position: 'absolute', maxWidth: 'none',
    width: `${100 / bw}%`, height: `${100 / bh}%`,
    left: `${(-bbox.x0 / bw) * 100}%`, top: `${(-bbox.y0 / bh) * 100}%`,
  };
  const done = () => { setLoaded(true); onLoad?.(); };

  let cropStyle: CSSProperties | null = null;
  if (!useFrame && size && size.w > 0 && size.h > 0) {
    const dst = geom.quad.map(([x, y]) => [((x - bbox.x0) / bw) * size.w, ((y - bbox.y0) / bh) * size.h] as Pt);
    try {
      const H = perspectiveTransform([[0, 0], [size.w, 0], [size.w, size.h], [0, size.h]], dst);
      cropStyle = {
        position: 'absolute', left: 0, top: 0, width: size.w, height: size.h, maxWidth: 'none',
        transformOrigin: '0 0',
        transform: `matrix3d(${H[0]},${H[3]},0,${H[6]},${H[1]},${H[4]},0,${H[7]},0,0,1,0,${H[2]},${H[5]},0,${H[8]})`,
      };
    } catch { cropStyle = null; }
  }

  return (
    <div ref={ref} className="relative size-full overflow-hidden bg-black">
      {useFrame ? (
        <img
          src={frameUrl!}
          alt=""
          decoding="async"
          onLoad={done}
          onError={() => setFrameFailed(true)}
          className="transition-opacity duration-200"
          style={{ ...frameBoxStyle, opacity: loaded ? 1 : 0 }}
        />
      ) : cropUrl && cropStyle ? (
        <>
        {bgFrameUrl && !bgFailed && (
          <img src={bgFrameUrl} alt="" decoding="async" onError={() => setBgFailed(true)}
            className="transition-opacity duration-200" style={{ ...frameBoxStyle, opacity: loaded ? 1 : 0 }} />
        )}
        <img
          src={cropUrl}
          alt=""
          decoding="async"
          onLoad={done}
          className="transition-opacity duration-200"
          style={{ ...cropStyle, opacity: loaded ? 1 : 0 }}
        />
        </>
      ) : null}
    </div>
  );
}
